import type { EnvironmentConnectionPhase } from "@t3tools/client-runtime/connection";
import type { CloudEnvironmentControlResult } from "@t3tools/contracts";

export type CloudSetupState =
  | "loading"
  | "setup"
  | "select"
  | "stopped"
  | "start"
  | "starting"
  | "authorize"
  | "resume"
  | "resuming"
  | "ready"
  | "error";

export type CloudSetupAction = "setup" | "start" | "resume" | "retry" | null;
export type FabricaAccountState = "unconfigured" | "loading" | "signed-out" | "signed-in";
export type CloudSetupOperation = "sign-in" | "provision" | "retry" | "open-connections" | null;

/**
 * Runtime control is intentionally reconciled a small, finite number of times.
 * The managed control response can arrive before relay discovery sees the new
 * environment, but a stuck Connect flow must eventually become an explicit
 * Retry instead of an endless timer loop.
 */
export const CLOUD_CONNECT_RECONCILIATION_DELAYS_MS = [250, 500, 1_000] as const;

export interface CloudSetupPresentation {
  readonly state: CloudSetupState;
  readonly label: string;
  readonly description: string;
  readonly action: CloudSetupAction;
  readonly actionLabel: string | null;
  readonly tone: "muted" | "success" | "warning" | "error";
}

export interface CloudSetupStateInput {
  /** Whether the environment catalog has finished its first hydration. */
  readonly catalogReady?: boolean;
  /** The selected server advertised itself as an AgentCore-backed environment. */
  readonly selectedEnvironmentIsCloud: boolean;
  /** Number of explicitly cloud-placed environments known to this client. */
  readonly cloudEnvironmentCount: number;
  /** The selected environment's live registry connection phase. */
  readonly connectionPhase: EnvironmentConnectionPhase | null;
  /** The latest response from the managed AgentCore control route. */
  readonly runtimeControlState?: CloudEnvironmentControlResult["state"] | null;
  /** Whether bounded discovery reconciliation is still pending or exhausted. */
  readonly runtimeControlReconciliation?: "pending" | "exhausted";
}

/**
 * Resolve the side effect for the visible action without coupling the view to
 * a particular environment implementation. Starting or resuming a selected
 * Cloud target must go through the managed AgentCore control route; registry
 * retry is reserved for an already-running runtime whose transport failed.
 */
export function resolveCloudSetupOperation(input: {
  readonly selectedEnvironmentIsCloud: boolean;
  readonly action: CloudSetupAction;
  readonly accountState: FabricaAccountState;
  readonly relayReady: boolean;
  readonly hasSignIn: boolean;
  readonly hasProvisionCloud: boolean;
  readonly hasRetryEnvironment: boolean;
  readonly hasOpenConnections: boolean;
}): CloudSetupOperation {
  if (input.action === null) return null;

  const gateAccountAndRelay = (): "ready" | "sign-in" | null => {
    if (input.accountState === "signed-out") {
      return input.hasSignIn ? "sign-in" : null;
    }
    if (input.accountState !== "signed-in") return null;
    return input.relayReady ? "ready" : null;
  };

  // Setup/start/resume always establish account ownership before inspecting
  // the currently available control handlers. An empty environment catalog
  // has no provision handler yet, but its first click must still open Clerk
  // rather than silently falling through to Connections.
  if (input.action === "setup" || input.action === "start" || input.action === "resume") {
    const readiness = gateAccountAndRelay();
    if (readiness !== "ready") return readiness;
  }

  if (input.selectedEnvironmentIsCloud) {
    if (input.action === "start" || input.action === "resume") {
      return input.hasProvisionCloud ? "provision" : null;
    }
    return input.action === "retry" && input.hasRetryEnvironment ? "retry" : null;
  }
  if (input.hasProvisionCloud) {
    // The unavailable-runtime retry fallback can also start AgentCore, so it
    // must apply the same gate even though its visible action is "retry".
    const readiness = gateAccountAndRelay();
    return readiness === "ready" ? "provision" : readiness;
  }
  if (input.action === "setup") {
    return input.hasOpenConnections ? "open-connections" : null;
  }
  return input.hasRetryEnvironment ? "retry" : null;
}

/**
 * Keep the Cloud lifecycle vocabulary in one place. A missing placement is
 * deliberately treated as local/unknown: older servers must never become
 * billable merely because a client is new enough to know about Cloud.
 */
export function presentCloudSetupState(input: CloudSetupStateInput): CloudSetupPresentation {
  if (input.catalogReady === false) {
    return {
      state: "loading",
      label: "Loading Cloud",
      description: "Reading connected execution environments.",
      action: null,
      actionLabel: null,
      tone: "muted",
    };
  }

  // A runtime control response belongs to the managed control flow. Once a
  // Cloud environment is selected, its connection phase is authoritative.
  if (!input.selectedEnvironmentIsCloud) {
    switch (input.runtimeControlState as string | null | undefined) {
      case "unavailable":
        return {
          state: "error",
          label: "Cloud needs configuration",
          description:
            "The managed Fabrica Cloud service cannot reach its runtime yet. Retry, or contact Fabrica support if the problem continues.",
          action: "retry",
          actionLabel: "Retry Cloud",
          tone: "error",
        };
      case "needs_connect_authorization":
        return {
          state: "authorize",
          label: "Authorization required",
          description:
            "The AgentCore runtime is running. Authorize Fabrica Connect once so this Cloud environment can appear on your devices.",
          action: null,
          actionLabel: null,
          tone: "warning",
        };
      case "connecting":
        if (input.runtimeControlReconciliation === "exhausted") {
          return {
            state: "error",
            label: "Cloud connection needs a retry",
            description:
              "Cloud authorization completed, but this client could not discover the environment yet. Retry when you are ready.",
            action: "retry",
            actionLabel: "Retry Cloud",
            tone: "error",
          };
        }
        return {
          state: "starting",
          label: "Connecting Cloud",
          description:
            "Authorization succeeded. The runtime is connecting through Fabrica Connect now.",
          action: null,
          actionLabel: null,
          tone: "warning",
        };
      case "ready":
        return {
          state: "ready",
          label: "Cloud ready",
          description:
            "The AgentCore runtime is online. It will appear as a Cloud environment through Fabrica Connect.",
          action: null,
          actionLabel: null,
          tone: "success",
        };
      case "stopped":
        return {
          state: "stopped",
          label: "Cloud stopped",
          description:
            "The isolated Cloud runtime is stopped. Start it when you want to run a Cloud task; Local remains the default target.",
          action: "start",
          actionLabel: "Start Cloud",
          tone: "muted",
        };
      case null:
      case undefined:
        break;
    }
  }

  if (!input.selectedEnvironmentIsCloud) {
    if (input.cloudEnvironmentCount > 0) {
      return {
        state: "select",
        label: "Select Cloud to continue",
        description:
          "Cloud is available for this client. Choose the Cloud environment above to start or resume it; Local remains the default and free target.",
        action: null,
        actionLabel: null,
        tone: "muted",
      };
    }

    return {
      state: "setup",
      label: "Cloud is not set up",
      description:
        "Start an isolated AgentCore runtime and connect it to this Fabrica account. Local execution remains the default and free target.",
      action: "setup",
      actionLabel: "Set up Cloud",
      tone: "muted",
    };
  }

  switch (input.connectionPhase ?? "available") {
    case "available":
      return {
        state: "start",
        label: "Ready to start",
        description:
          "This Cloud environment is registered but not connected. Start it when you want to run a Cloud task.",
        action: "start",
        actionLabel: "Start Cloud",
        tone: "warning",
      };
    case "offline":
      return {
        state: "resume",
        label: "Paused",
        description:
          "This Cloud environment is offline. Resume it to restore its workspace and provider sessions.",
        action: "resume",
        actionLabel: "Resume Cloud",
        tone: "warning",
      };
    case "connecting":
      return {
        state: "starting",
        label: "Starting Cloud",
        description: "Starting the isolated Cloud runtime and reconnecting its Fabrica server.",
        action: null,
        actionLabel: null,
        tone: "warning",
      };
    case "reconnecting":
      return {
        state: "resuming",
        label: "Resuming Cloud",
        description: "Restoring the Cloud runtime connection. Your local target is unaffected.",
        action: null,
        actionLabel: null,
        tone: "warning",
      };
    case "connected":
      return {
        state: "ready",
        label: "Cloud ready",
        description:
          "The Cloud environment is connected. Provider sessions and workspace state stay in that environment.",
        action: null,
        actionLabel: null,
        tone: "success",
      };
    case "error":
      return {
        state: "error",
        label: "Cloud unavailable",
        description:
          "Fabrica could not connect to this Cloud environment. Retry the connection before starting a Cloud task.",
        action: "retry",
        actionLabel: "Retry Cloud",
        tone: "error",
      };
  }
}

export function cloudProviderSetupLabel(input: {
  readonly selectedEnvironmentIsCloud: boolean;
  readonly cloudSetupState: CloudSetupState;
}): string {
  if (!input.selectedEnvironmentIsCloud) {
    return "Available after Cloud is connected";
  }
  switch (input.cloudSetupState) {
    case "ready":
      return "Connect it in Providers";
    case "authorize":
      return "Authorize Fabrica Connect before configuring this provider";
    case "start":
    case "stopped":
    case "starting":
    case "resume":
    case "resuming":
    case "error":
      return "Connect Cloud before configuring this provider";
    case "setup":
    case "select":
    case "loading":
      return "Select a Cloud environment first";
  }
}
